import { ThemeEvents } from '@theme/events';

const LETTERS_ONLY = /[^A-Za-z ]/g;

/**
 * Name inputs for personalised products. Renders one set of names per selected quantity
 * (1 name for single products, Name 1 + Name 2 for couple products).
 *
 * product-form.js calls getLines() on submit and adds each piece as its own qty-1 cart line.
 * Inputs also carry regular `properties[...]` names so non-AJAX checkouts (Buy it now) still
 * record every name on one line.
 */
class PersonalizationNames extends HTMLElement {
  /** @type {HTMLElement | null} */
  #productForm = null;

  connectedCallback() {
    this.#productForm = this.closest('product-form-component');
    this.#productForm?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#sync);
    this.#productForm?.addEventListener('input', this.#onFormInput);
    this.#sync();
  }

  disconnectedCallback() {
    this.#productForm?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#sync);
    this.#productForm?.removeEventListener('input', this.#onFormInput);
  }

  get #isCouple() {
    return this.dataset.mode === 'couple';
  }

  get #maxLength() {
    return Number(this.dataset.maxLength) || 10;
  }

  get #keys() {
    return this.#isCouple ? ['Name 1', 'Name 2'] : ['Name'];
  }

  get #quantity() {
    const input = /** @type {HTMLInputElement | null} */ (
      this.#productForm?.querySelector('input[name="quantity"]') ?? null
    );
    return Math.max(1, parseInt(input?.value ?? '1') || 1);
  }

  get #container() {
    return /** @type {HTMLElement} */ (this.querySelector('[data-pieces]'));
  }

  /** @param {Event} event */
  #onFormInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.name === 'quantity') {
      this.#sync();
      return;
    }

    if (!this.contains(target)) return;
    const cleaned = target.value.replace(LETTERS_ONLY, '').slice(0, this.#maxLength);
    if (cleaned !== target.value) target.value = cleaned;
    if (cleaned.trim()) this.#setError(target, '');
  };

  /** Adds or removes piece groups so their count matches the quantity. */
  #sync = () => {
    const container = this.#container;
    const quantity = this.#quantity;

    while (container.children.length < quantity) {
      container.appendChild(this.#createPiece(container.children.length + 1));
    }
    while (container.children.length > quantity) {
      container.lastElementChild?.remove();
    }

    this.#updateLabels(quantity);
  };

  /** @param {number} index */
  #createPiece(index) {
    const piece = document.createElement('div');
    piece.className = 'personalization-names__piece';
    piece.dataset.piece = String(index);

    const title = document.createElement('p');
    title.className = 'personalization-names__piece-title';
    piece.appendChild(title);

    const row = document.createElement('div');
    row.className = 'personalization-names__row';

    for (const key of this.#keys) {
      const id = `PersonalizationName-${this.#productForm?.dataset.sectionId ?? ''}-${index}-${key.replace(' ', '')}`;

      const field = document.createElement('div');
      field.className = 'personalization-names__field';

      const label = document.createElement('label');
      label.htmlFor = id;
      label.textContent = key;

      const input = document.createElement('input');
      input.type = 'text';
      input.id = id;
      input.className = 'field__input';
      input.dataset.key = key;
      input.required = true;
      input.maxLength = this.#maxLength;
      input.pattern = '[A-Za-z ]+';
      input.autocomplete = 'off';
      input.placeholder = key;
      input.setAttribute('aria-required', 'true');

      const error = document.createElement('p');
      error.className = 'personalization-names__error';
      error.hidden = true;

      field.append(label, input, error);
      row.appendChild(field);
    }

    piece.appendChild(row);
    return piece;
  }

  /** Piece titles and fallback property names depend on the total quantity. @param {number} quantity */
  #updateLabels(quantity) {
    for (const piece of /** @type {HTMLElement[]} */ (Array.from(this.#container.children))) {
      const title = /** @type {HTMLElement} */ (piece.querySelector('.personalization-names__piece-title'));
      title.textContent = `Piece ${piece.dataset.piece}`;
      title.hidden = quantity === 1;

      for (const input of piece.querySelectorAll('input')) {
        const key = input.dataset.key ?? 'Name';
        const propertyKey = quantity === 1 ? key : `Piece ${piece.dataset.piece} - ${key}`;
        input.name = `properties[${propertyKey}]`;
      }
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @param {string} message
   */
  #setError(input, message) {
    const error = /** @type {HTMLElement | null} */ (input.parentElement?.querySelector('.personalization-names__error'));
    input.setAttribute('aria-invalid', message ? 'true' : 'false');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  /**
   * Validates every name and returns one properties object per piece, or null if invalid.
   * @returns {Array<Record<string, string>> | null}
   */
  getLines() {
    this.#sync();

    /** @type {Array<Record<string, string>>} */
    const lines = [];
    /** @type {HTMLInputElement | null} */
    let firstInvalid = null;

    for (const piece of Array.from(this.#container.children)) {
      /** @type {Record<string, string>} */
      const properties = {};

      for (const input of piece.querySelectorAll('input')) {
        const value = input.value.replace(LETTERS_ONLY, '').trim().slice(0, this.#maxLength);
        if (!value) {
          this.#setError(input, 'Please enter a name');
          firstInvalid ??= input;
        } else {
          this.#setError(input, '');
        }
        properties[input.dataset.key ?? 'Name'] = value;
      }

      lines.push(properties);
    }

    if (firstInvalid) {
      firstInvalid.focus();
      return null;
    }

    return lines;
  }
}

if (!customElements.get('personalization-names')) {
  customElements.define('personalization-names', PersonalizationNames);
}
